import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { toast } from 'sonner'

import { useEffect, useMemo, useState } from 'react'

import { PosReportModel, type DailyProfitLoss } from '@/http/models/PosReport'
import { useBusiness } from '@/contexts/BusinessContext'
import { apiErrorMessage } from '@/lib/apiError'

import { TableCell, TableFooter, TableRow } from '@/components/ui/table'

import { PageHeader, StatsCards } from '@/components/atoms'
import { ChartCard, ChartLegend, ChartTooltip, chartAxis, chartGrid, compactNumber } from '@/components/dashboard/chart-kit'
import { FilterSelect } from '@/components/features/pos/FilterSelect'
import { PosListCard } from '@/components/features/pos/PosListCard'
import { usePosLookups } from '@/components/features/pos/usePosLookups'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { useReportRange } from '@/components/reports/useReportRange'

const today = () => new Date().toLocaleDateString('en-CA')

const margin = (revenue: number, cost: number) => (revenue ? `${(((revenue - cost) / revenue) * 100).toFixed(1)}%` : '—')

/**
 * Daily POS revenue against purchase cost, per branch (`daily_profit_losses`, kept by sync; checklist C26). The
 * per-product rows (`/profit-loss/list-by-product`) carry only variation ids, so they are not shown yet.
 */
const ProfitLoss = () => {
  const { formatCurrency } = useBusiness()
  const lookups = usePosLookups()
  const { preset, range, onPresetChange, onRangeChange } = useReportRange()
  const [branch, setBranch] = useState('')
  const [rows, setRows] = useState<DailyProfitLoss[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (range.from_date > range.to_date) return
    let cancelled = false
    setLoading(true)
    PosReportModel.getProfitLoss({ ...range, branch_id: branch || undefined }).then((res) => {
      if (cancelled) return
      if (res.success && res.data) setRows(res.data.result ?? [])
      else toast.error(apiErrorMessage(res, 'Could not load profit and loss'))
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [range, branch])

  const totals = useMemo(
    () => rows.reduce((t, r) => ({ revenue: t.revenue + r.sales_revenue, cost: t.cost + r.purchase_cost }), { revenue: 0, cost: 0 }),
    [rows],
  )
  // One bar per day across the chosen branches: cost below, gross profit on top, so the bar is the revenue.
  const byDay = useMemo(() => {
    const days = new Map<string, { date: string; cost: number; profit: number }>()
    for (const r of rows) {
      const d = days.get(r.date) ?? { date: r.date, cost: 0, profit: 0 }
      d.cost += r.purchase_cost
      d.profit += r.sales_revenue - r.purchase_cost
      days.set(r.date, d)
    }
    return [...days.values()].sort((a, b) => a.date.localeCompare(b.date))
  }, [rows])
  const money = (n: number) => <span className="tabular-nums">{formatCurrency(n)}</span>

  return (
    <div className="space-y-4">
      <PageHeader title="Profit and loss" description="POS sales revenue against the purchase cost of what was sold, by day and branch." />
      <ReportToolbar preset={preset} range={range} onPresetChange={onPresetChange} onRangeChange={onRangeChange} />

      <StatsCards
        stats={[
          { title: 'Revenue', value: formatCurrency(totals.revenue) },
          { title: 'Cost', value: formatCurrency(totals.cost) },
          { title: 'Gross profit', value: formatCurrency(totals.revenue - totals.cost) },
          { title: 'Margin', value: margin(totals.revenue, totals.cost) },
        ]}
      />

      <ChartCard
        title="Revenue and cost by day"
        value={formatCurrency(totals.revenue)}
        subheader="Each bar is the day's revenue: cost of what was sold, then gross profit"
        empty={!byDay.length}
        legend={
          <ChartLegend
            items={[
              { label: 'Cost', value: formatCurrency(totals.cost), color: 'hsl(var(--chart-3))' },
              { label: 'Gross profit', value: formatCurrency(totals.revenue - totals.cost), color: 'hsl(var(--chart-1))' },
            ]}
          />
        }
        table={{ columns: ['Date', 'Revenue'], rows: byDay.map((d) => [d.date, formatCurrency(d.cost + d.profit)]) }}
      >
        <BarChart data={byDay} margin={{ left: 0, right: 8 }}>
          <CartesianGrid {...chartGrid} />
          <XAxis dataKey="date" {...chartAxis} minTickGap={24} />
          <YAxis {...chartAxis} tickFormatter={compactNumber} width={48} />
          <Tooltip content={<ChartTooltip format={(v) => formatCurrency(Number(v))} />} />
          <Bar dataKey="cost" name="Cost" stackId="day" fill="hsl(var(--chart-3))" stroke="hsl(var(--card))" strokeWidth={2} />
          <Bar dataKey="profit" name="Gross profit" stackId="day" fill="hsl(var(--chart-1))" stroke="hsl(var(--card))" strokeWidth={2} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartCard>

      <PosListCard<DailyProfitLoss>
        rows={rows}
        loading={loading}
        empty="No sales in these dates."
        filters={<FilterSelect label="Branch" value={branch} onChange={setBranch} allLabel="All branches" options={lookups.branches.map((b) => ({ label: b.name, value: b._id }))} />}
        columns={[
          {
            label: 'Date',
            render: (r) => (
              <span>
                {r.date}
                {r.date === today() && <span className="ml-1.5 text-xs text-muted-foreground">today, so far</span>}
              </span>
            ),
          },
          { label: 'Branch', render: (r) => lookups.branchName(r.branch_id) },
          { label: 'Revenue', render: (r) => money(r.sales_revenue), className: 'text-right' },
          { label: 'Cost', render: (r) => money(r.purchase_cost), className: 'text-right' },
          { label: 'Profit', render: (r) => money(r.sales_revenue - r.purchase_cost), className: 'text-right' },
          { label: 'Margin', render: (r) => margin(r.sales_revenue, r.purchase_cost), className: 'hidden text-right md:table-cell' },
        ]}
        footer={
          rows.length > 0 && !loading ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2} className="font-medium">Total</TableCell>
                <TableCell className="text-right">{money(totals.revenue)}</TableCell>
                <TableCell className="text-right">{money(totals.cost)}</TableCell>
                <TableCell className="text-right">{money(totals.revenue - totals.cost)}</TableCell>
                <TableCell className="hidden text-right md:table-cell">{margin(totals.revenue, totals.cost)}</TableCell>
              </TableRow>
            </TableFooter>
          ) : undefined
        }
      />
    </div>
  )
}

export default ProfitLoss
